
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';

import ApplicationContext from './contexts/application';
import { getToken, getTokenViaRbac } from './libs/apiCalls';
import { useMsal } from '@azure/msal-react';
import { useRbacClient } from '@e-magic-inc/rbac-client/react';
import { isSsoEnabled } from './rbac/msal';
import { loginRequest } from './authConfig';
import { Alert, Container, FormControl, InputLabel, MenuItem, useMediaQuery } from '@mui/material';

import { UserRoles } from './libs/userRoles';
import { config } from './libs/config';
import AuthLayout from './components/AuthLayout';
import BrandMark from './components/BrandMark';

const RBAC_TENANT_NAME = config.rbacTenantName;

/**
 * Turns a failed sign-in into a sentence worth showing someone.
 *
 * The three paths fail in three different shapes, which is how "Request failed with status
 * code 400" ended up on screen: RBAC rejects a bad username or password with an HTTP 400,
 * esb-data answers 200 with the reason in the body, and a service that is simply down
 * produces an error with no response at all. Only the middle one carries wording meant for
 * a person, so only that one is passed through.
 *
 * Deliberately does not distinguish an unknown username from a wrong password, whatever the
 * underlying service says - that difference tells an attacker which usernames exist.
 */
const describeSignInError = (error) => {
    if (error?.isServiceError && error.message) {
        return error.message;
    }

    // Two error shapes arrive here, and reading only one of them is how a wrong password
    // came to report itself as an unreachable service. The RBAC client is fetch-based and
    // throws its own HttpError carrying the status on the error itself; everything routed
    // through apiCalls is axios, which hangs it off error.response instead.
    const status = error?.status ?? error?.response?.status;

    if (status === 400 || status === 401 || status === 403) {
        return 'Incorrect username or password. Please try again.';
    }

    if (status) {
        return 'Something went wrong signing you in. Please try again, or contact your administrator if this continues.';
    }

    // No status in either shape means the request was never answered - the RBAC client's
    // NetworkError, or an axios error with no response.
    return 'Unable to reach the sign-in service. Please check your connection, or contact your administrator.';
};

function Login() {
    const { settings, saveSettings } = useContext(ApplicationContext);
    const { instance } = useMsal();
    const rbac = useRbacClient();
    const smallScreen = useMediaQuery('(min-height: 800px)');

    const navigate = useNavigate();

    const [signInError, setSignInError] = React.useState(null);

    // Never log the error object itself - getToken puts the password in a request header,
    // and axios hangs the whole request config off the error.
    const reportSignInError = (error) => {
        console.error('Sign-in failed', error?.status ?? error?.response?.status ?? '(no response)', error?.message);
        setSignInError(describeSignInError(error));
    };

    // In rbac mode this reflects what RBAC reports for this app/tenant; in local mode
    // initMsal() sets it from REACT_APP_SSO_ENABLED and the Entra client id/tenant.
    const enableSso = isSsoEnabled();

    const handleUserChange = (event) => {
        const value = event.target.value;
        if (Object.keys(settings).length > 0) {
            settings.userName = value;
        }
    };

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        if (Object.keys(settings).length > 0) {
            settings.password = value;
        }
    };

    // Every sign-in path converges here, but they do NOT all arrive with the same shape.
    // Only /auth/rbac-exchange populates roles; UserDTO documents that a local login leaves
    // it null and the caller derives roles from administrator/oem/operator/alarmGroups. Each
    // completeX below normalises its own response before calling this, so navigation and
    // persistence are written once.
    const applySession = async (user, oauth) => {
        settings.token = user.token;
        settings.roles = user.roles;
        settings.userName = user.userName;
        settings.name = user.name;
        if (oauth) {
            settings.oauth = oauth;
        }

        // Must finish before navigating — saveSettings persists to IndexedDB before
        // updating context state, so the next route's components (which read the token
        // from context) would otherwise mount and fire their own API calls against the
        // still-stale pre-login settings, 401, and get bounced to /signout.
        await saveSettings(settings);

        (settings.roles?.includes(UserRoles.ADMIN.label) || settings.roles?.includes(UserRoles.OEM.label)) ?
            navigate("/admin") :
            navigate("/user");
    };

    // Bridge into the existing backend session — esb-data verifies the RBAC token itself
    // (signature/issuer/expiry) rather than trusting a client-supplied username with a blank
    // password. Prefer the app-scoped token (carries this app's own OEM/SITE_ADMIN/OPERATORS/
    // USERS app_role claims); fall back to the base token for a multi-group user still
    // pending group selection, where no app token exists yet.
    const completeRbacLogin = (oauth) => {
        getTokenViaRbac(rbac.getAppToken() ?? rbac.getAccessToken(), async (response) => {
            await applySession(JSON.parse(response.data.result), oauth);
        }, (error) => {
            reportSignInError(error);
        });
    };

    // Local mode: username/password straight against the ESB user store.
    //
    // esb-data returns the user's flags and alarm groups rather than a roles list - see the
    // note on UserDTO.alarmGroups - so the roles array is derived here. Dropping this when the
    // sign-in paths were unified left settings.roles null, which crashed the admin layout on
    // the first render after login.
    const completeLocalLogin = () => {
        getToken(settings, async (response) => {
            const user = JSON.parse(response.data.result);

            let roles;
            if (user.oem) {
                roles = [UserRoles.OEM.label];
            }
            else if (user.administrator) {
                roles = [UserRoles.ADMIN.label];
            }
            else {
                // alarmGroups is already a list of role names on this branch.
                roles = [...(user.alarmGroups ?? [])];
                if (user.operator) {
                    roles.push(UserRoles.OPERATOR.label);
                }
            }

            await applySession({
                ...user,
                roles,
                // The local DTO carries firstName/lastName; only the RBAC path sets name.
                name: user.name || [user.firstName, user.lastName].filter(Boolean).join(' '),
            });
        }, (error) => {
            reportSignInError(error);
        });
    };

    const handleSubmit = async (event) => {
        setSignInError(null);

        if (config.authMode !== 'rbac') {
            completeLocalLogin();
            return;
        }

        try {
            await rbac.login(settings.userName, settings.password, RBAC_TENANT_NAME);
        } catch (error) {
            reportSignInError(error);
            return;
        }

        completeRbacLogin();
    };

    const handleLogin = async () => {
        try {
            await instance.initialize();

            if (config.authMode === 'rbac') {
                // Redirect flow: control returns to this page and is picked up by the
                // effect below, which hands the id token to RBAC.
                await instance.loginRedirect(loginRequest);
                return;
            }

            // Local mode: no RBAC to broker the token, so the Entra id token IS the session
            // token and the roles come from its claims. This is the pre-RBAC behaviour, kept
            // intact — esb-data already accepts these tokens for such deployments.
            const result = await instance.loginPopup(loginRequest);
            await applySession({
                token: result.idToken,
                roles: result.idTokenClaims?.roles ?? [],
                userName: result.account?.username,
                name: result.account?.name,
            }, "microsoft");
        } catch (e) {
            console.log(e);
        }
    };

    // MSAL's redirect flow returns to this same page (no dedicated bridge page, see
    // rbac/msal.js) — handleRedirectPromise() resolves non-null only on that return trip,
    // null on every ordinary page load. On a real result, the id token goes to RBAC
    // (source of truth for auth), then bridges into the same completeLogin() path as
    // password login above.
    React.useEffect(() => {
        // Only the RBAC path uses the redirect flow. Local mode signs in with a popup and
        // resolves inline in handleLogin(), so there is no return trip to intercept.
        if (config.authMode !== 'rbac') return;

        (async () => {
            try {
                await instance.initialize();
                const result = await instance.handleRedirectPromise();
                if (!result) return;

                await rbac.loginWithAzureToken(result.idToken, RBAC_TENANT_NAME);

                completeRbacLogin("microsoft");
            } catch (e) {
                // Not run through describeSignInError: an MSAL failure is not an axios error,
                // so it has no response to read and would fall through to the wrong branch.
                console.error('Microsoft sign-in failed', e?.message);
                setSignInError('Microsoft sign-in did not complete. Please try again, or contact your administrator.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <AuthLayout>
            <BrandMark src="/branding/logo.svg" style={{ width: '350px', marginTop: '20px', marginBottom: '30px' }} />

            <h2>Welcome</h2>

            {/*
              * The Entra block only appears where Entra is configured. It used to render a
              * permanently disabled button plus an "OR" divider on every deployment without
              * SSO, which is dead furniture - and it is why this screen did not match the GHD
              * one, whose form is just the credentials.
              */}
            {enableSso && (
                <>
                    <div>
                        <Button autoFocus variant="contained" onClick={handleLogin} sx={{ width: "100%" }}>Sign in with Microsoft</Button>
                    </div>
                    <div>
                        <h3>OR</h3>
                    </div>
                </>
            )}

            {/*
              * Outside the Entra block, because it is about the form below it rather than
              * about the choice between two ways in.
              *
              * It was moved in there when the permanently disabled Microsoft button and its
              * OR divider were taken off the deployments with no SSO - correctly, they were
              * furniture - and this went with them, so every site that signs in with a
              * username lost the one line telling them to.
              *
              * Not at GHD, whose sign-in is deliberately just the two fields.
              */}
            {config.dashboard !== 'ghd' && (
                <h5>Please login with your credentials to access the system.</h5>
            )}

            {signInError && (
                <Alert severity="error" sx={{ width: '100%', mb: 1, textAlign: 'left' }}>{signInError}</Alert>
            )}

            <div>
                <TextField required label="Username" sx={{ width: "100%" }} onChange={handleUserChange} />
                <TextField required label="Password" type="password" autoComplete="off" sx={{ width: "100%" }} onChange={handlePasswordChange} onKeyDown={(e) => { if (e.key === 'Enter') { handleSubmit(e); } }} />
                <Button variant="contained" onClick={(e) => handleSubmit(e)} sx={{ width: "100%" }}>Login</Button>
            </div>
        </AuthLayout>
    );
}

export default Login;

